import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CATALOG_MUSCLE_SLUGS,
  DEFAULT_TARGET_BAND,
  formatTargetMetricValue,
  fromCanonicalKg,
  isScoreableTarget,
  muscleKey,
  targetBand,
  toCanonicalKg,
  weeklyTargetActualStats,
  type Exercise,
  type TargetActualStats,
  type TargetAxis,
  type TargetItem,
  type TargetMetric,
  type TimedSessionResult,
  type UnitsPreference,
} from "@heraion/shared";

import type { SelectOption } from "../ProgramEditor/Select";
import { weightUnitLabels } from "../weightUnitLabels";

// AP-1b/AP-1b2/AP-1b-u/AP-1b-b — the weekly-targets editor state machine, split
// out of WeeklyTargetsSection (which stays a pure view under the 500-line cap,
// same shape as useMaxEditor / useEditInPlace). Owns vocab, uniqueness, mutation,
// unit conversion (tonnage goals edited in the user's unit, saved as kg), the
// per-target band editing, and the reference-stats compute. The view renders
// what this returns; it never touches the model directly.

export const AXES: readonly TargetAxis[] = ["muscle", "exercise"];
export const METRICS: readonly TargetMetric[] = ["hard-sets", "tonnage"];

export const METRIC: Record<
  TargetMetric,
  { chip: string; unit: string; defaultValue: number }
> = {
  "hard-sets": {
    chip: "composer.targetMetricHardSets",
    unit: "composer.targetUnitHardSets",
    defaultValue: 10,
  },
  tonnage: {
    chip: "composer.targetMetricTonnage",
    unit: "composer.targetUnitTonnage",
    defaultValue: 5000,
  },
  // `reps` kept in the type but never offered by the editor (ungroundable).
  reps: {
    chip: "composer.targetMetricReps",
    unit: "composer.targetUnitReps",
    defaultValue: 100,
  },
};

// Default band as symmetric percentages (±10%) — the seed for the per-target
// band editor and the fallback label.
const DEFAULT_BELOW = Math.round((1 - DEFAULT_TARGET_BAND[0]) * 100);
const DEFAULT_ABOVE = Math.round((DEFAULT_TARGET_BAND[1] - 1) * 100);

// The meter's own rule (weeklyTargets.ts); legacy pattern / reps do not score.
export const isScored = isScoreableTarget;

// A target's band as editor percentages below/above the goal.
const bandPercents = (it: TargetItem): { below: number; above: number } => {
  const [lo, hi] = targetBand(it);
  return { below: Math.round((1 - lo) * 100), above: Math.round((hi - 1) * 100) };
};

type Args = {
  items: TargetItem[];
  onChange: (items: TargetItem[]) => void;
  unitsPreference: UnitsPreference;
  programExercises: { slug: string; name: string }[];
  sessionResults?: TimedSessionResult[];
  exerciseCatalog?: ReadonlyArray<Exercise>;
  // AP-7: average working sets/week per muscle the program itself prescribes
  // (programWeeklyPrescription). The editor shows it next to the actual so a
  // hard-set goal reads against both what you do and what the program asks.
  prescription?: Record<string, number>;
};

export function useWeeklyTargetsEditor({
  items,
  onChange,
  unitsPreference,
  programExercises,
  sessionResults,
  exerciseCatalog,
  prescription,
}: Args) {
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const locale = i18n.language;
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [valueText, setValueText] = useState("");
  // Band editing buffers (AP-1b-b): percentages below/above the goal.
  const [belowText, setBelowText] = useState(String(DEFAULT_BELOW));
  const [aboveText, setAboveText] = useState(String(DEFAULT_ABOVE));

  // Tonnage goals are edited in the user's unit (display lb / save kg); hard-set
  // and rep goals are unit-less counts. One place decides which.
  const displayUnitFor = (metric: TargetMetric): UnitsPreference | null =>
    metric === "tonnage" ? unitsPreference : null;
  const toDisplayValue = (value: number, metric: TargetMetric): number => {
    const u = displayUnitFor(metric);
    return u ? fromCanonicalKg(value, u) : value;
  };
  const toModelValue = (raw: number, metric: TargetMetric): number => {
    const u = displayUnitFor(metric);
    return u ? toCanonicalKg(raw, u) : raw;
  };
  // The shared display formatter (locale-grouped, tonnage compacted ≥10k, unit
  // included) — the SAME one the Home meter uses, so a tonnage goal reads
  // identically in both places. `value` is canonical (kg for tonnage); the
  // formatter converts + formats. Used for every READ-ONLY render; the editable
  // TextField buffer stays raw digits (a text input can't carry separators).
  const formatMetricValue = (value: number, metric: TargetMetric): string =>
    formatTargetMetricValue(value, metric, unitsPreference, locale, unitLabels);
  const unitLabel = (it: TargetItem): string =>
    it.metric === "tonnage" ? unitsPreference : t(METRIC[it.metric].unit);
  // Read-only "value + unit" for the collapsed row / summary. Tonnage carries
  // its unit in the formatted string; a count appends the i18n unit ("hard sets").
  const displayValueWithUnit = (it: TargetItem): string => {
    const s = formatMetricValue(it.value, it.metric);
    return it.metric === "tonnage" ? s : `${s} ${t(METRIC[it.metric].unit)}`;
  };

  const bandLabel = (it: TargetItem): string => {
    const { below, above } = bandPercents(it);
    return below === above
      ? t("composer.targetBand", { pct: below, defaultValue: "±{{pct}}%" })
      : t("composer.targetBandAsym", {
          lo: below,
          hi: above,
          defaultValue: "−{{lo}}% / +{{hi}}%",
        });
  };

  const seedBand = (it: TargetItem) => {
    const { below, above } = bandPercents(it);
    setBelowText(String(below));
    setAboveText(String(above));
  };

  const exerciseName = useMemo(() => {
    const m = new Map(programExercises.map((e) => [e.slug, e.name]));
    return (slug: string) => m.get(slug) ?? slug;
  }, [programExercises]);

  const axisVocab = (axis: TargetAxis): readonly string[] =>
    axis === "muscle" ? CATALOG_MUSCLE_SLUGS : programExercises.map((e) => e.slug);

  const keyLabel = (axis: TargetAxis, key: string): string =>
    axis === "muscle"
      ? t(muscleKey(key as (typeof CATALOG_MUSCLE_SLUGS)[number]))
      : exerciseName(key);

  const firstFreeKey = (
    axis: TargetAxis,
    metric: TargetMetric,
    exceptIdx: number,
  ): string | undefined => {
    const used = new Set(
      items
        .filter((o, idx) => idx !== exceptIdx && o.axis === axis && o.metric === metric)
        .map((o) => o.key),
    );
    return axisVocab(axis).find((k) => !used.has(k));
  };

  const patch = (i: number, changes: Partial<TargetItem>) =>
    onChange(items.map((it, idx) => (idx === i ? ({ ...it, ...changes } as TargetItem) : it)));

  const beginEdit = (i: number) => {
    const it = items[i];
    if (it) {
      setValueText(String(toDisplayValue(it.value, it.metric)));
      seedBand(it);
    }
    setEditingIdx(i);
  };

  const addTarget = () => {
    // Default to a scored muscle × hard-sets target; fall back through the
    // remaining combos so "Add" always yields a schema-unique item.
    const combos: Array<[TargetAxis, TargetMetric]> = [
      ["muscle", "hard-sets"],
      ["muscle", "tonnage"],
      ["exercise", "hard-sets"],
      ["exercise", "tonnage"],
    ];
    for (const [axis, metric] of combos) {
      const key = firstFreeKey(axis, metric, -1);
      if (key) {
        const next: TargetItem[] = [
          ...items,
          { axis, key, metric, value: METRIC[metric].defaultValue },
        ];
        onChange(next);
        setValueText(String(toDisplayValue(METRIC[metric].defaultValue, metric)));
        setBelowText(String(DEFAULT_BELOW));
        setAboveText(String(DEFAULT_ABOVE));
        setEditingIdx(next.length - 1);
        return true;
      }
    }
    return false;
  };

  const remove = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i));
    setEditingIdx(null);
  };

  const changeAxis = (i: number, axis: TargetAxis) => {
    const it = items[i];
    if (!it || it.axis === axis) return;
    // Move to a free key of the new axis; refuse the switch if its vocab is
    // exhausted (never emit a colliding (axis,key,metric)).
    const key = firstFreeKey(axis, it.metric, i);
    if (key === undefined) return;
    patch(i, { axis, key });
  };

  // Disable the other axis' chip when it has no selectable key (vocab empty —
  // e.g. `exercise` on a program with no exercises yet — or fully used).
  const axisDisabled = (i: number, axis: TargetAxis): boolean => {
    const it = items[i];
    if (!it || it.axis === axis) return false;
    return firstFreeKey(axis, it.metric, i) === undefined;
  };

  const changeMetric = (i: number, metric: TargetMetric) => {
    const it = items[i];
    if (!it || it.metric === metric) return;
    const collides = items.some(
      (o, idx) => idx !== i && o.axis === it.axis && o.key === it.key && o.metric === metric,
    );
    const key = collides ? (firstFreeKey(it.axis, metric, i) ?? it.key) : it.key;
    const value = METRIC[metric].defaultValue;
    setValueText(String(toDisplayValue(value, metric)));
    patch(i, { metric, key, value });
  };

  const metricTaken = (i: number, metric: TargetMetric): boolean => {
    const it = items[i];
    if (!it) return false;
    return items.some(
      (o, idx) => idx !== i && o.axis === it.axis && o.key === it.key && o.metric === metric,
    );
  };

  const onValueChange = (i: number, raw: string) => {
    setValueText(raw);
    const it = items[i];
    if (!it) return;
    const n = Number(raw.replace(",", "."));
    if (Number.isFinite(n) && n > 0) {
      const model = toModelValue(n, it.metric);
      if (model > 0 && model <= 1_000_000) patch(i, { value: model });
    }
  };
  const onValueBlur = (i: number) => {
    const it = items[i];
    if (it) setValueText(String(toDisplayValue(it.value, it.metric)));
  };

  // Band editing (AP-1b-b): commit a valid (below, above) pair as multipliers.
  const commitBand = (i: number, below: string, above: string) => {
    const b = Math.round(Number(below.replace(",", ".")));
    const a = Math.round(Number(above.replace(",", ".")));
    // 0 < low ≤ high needs below < 100 and above ≥ 0 (below is % under the goal).
    if (!Number.isFinite(b) || !Number.isFinite(a) || b < 0 || b >= 100 || a < 0) return;
    const isDefault = b === DEFAULT_BELOW && a === DEFAULT_ABOVE;
    patch(i, { band: isDefault ? undefined : [1 - b / 100, 1 + a / 100] });
  };
  const onBelowChange = (i: number, raw: string) => {
    setBelowText(raw);
    commitBand(i, raw, aboveText);
  };
  const onAboveChange = (i: number, raw: string) => {
    setAboveText(raw);
    commitBand(i, belowText, raw);
  };
  const onBandBlur = (i: number) => {
    const it = items[i];
    if (it) seedBand(it);
  };

  // Live validity of the band buffers, for visible field feedback (below is a
  // %, so 0–99 keeps low > 0; above is any ≥ 0).
  const parseBand = (raw: string): number => Math.round(Number(raw.replace(",", ".")));
  const belowInvalid = (() => {
    const b = parseBand(belowText);
    return !Number.isFinite(b) || b < 0 || b >= 100;
  })();
  const aboveInvalid = (() => {
    const a = parseBand(aboveText);
    return !Number.isFinite(a) || a < 0;
  })();

  const keyOptions = (i: number): SelectOption[] => {
    const it = items[i];
    if (!it) return [];
    const used = new Set(
      items
        .filter((o, idx) => idx !== i && o.axis === it.axis && o.metric === it.metric)
        .map((o) => o.key),
    );
    const opts = axisVocab(it.axis)
      .filter((k) => k === it.key || !used.has(k))
      .map((k) => ({ value: k, label: keyLabel(it.axis, k) }));
    // Legacy safety: a stored key outside the current axis' vocab (an old
    // pattern-axis target, or an exercise dropped from the program) would leave
    // the Select blank. Surface it so the saved value stays visible and the user
    // can re-pick a supported axis instead of seeing an empty field.
    if (!opts.some((o) => o.value === it.key)) {
      opts.unshift({ value: it.key, label: keyLabel(it.axis, it.key) });
    }
    return opts;
  };

  // Reference stats for the row being edited — the athlete's own weekly actual
  // for this (axis, key, metric). Null when the host didn't wire history/catalog
  // (web) or for a legacy pattern target.
  const editing = editingIdx != null ? items[editingIdx] : undefined;
  const stats: TargetActualStats | null = useMemo(() => {
    if (!editing || editing.axis === "pattern") return null;
    if (!sessionResults || !exerciseCatalog) return null;
    return weeklyTargetActualStats(
      sessionResults,
      exerciseCatalog,
      editing,
      Date.now(),
    );
    // Recompute only when the target identity or the data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editing?.axis,
    editing?.key,
    editing?.metric,
    sessionResults,
    exerciseCatalog,
  ]);

  // What the program itself prescribes for the edited target's muscle (AP-7):
  // planned working sets/week, so the goal sits next to both "what you do" and
  // "what the program asks". Muscle×hard-sets only (the prescription is a
  // per-muscle hard-set count); null otherwise or when the host didn't wire it.
  const prescribed: number | null =
    editing && editing.axis === "muscle" && editing.metric === "hard-sets"
      ? (prescription?.[editing.key] ?? null)
      : null;

  const useAvg = (i: number) => {
    const it = items[i];
    // PG-17: the average of the 4 COMPLETE weeks, never one dragged down by
    // the partial current week. No complete week → nothing to seed from.
    if (!stats?.hasFullWeek || !it) return;
    // avgPerWeek is in the metric's native unit (kg for tonnage) = the model
    // value; the field shows it converted to the display unit.
    const v = Math.max(1, Math.round(stats.avgPerWeek));
    setValueText(String(toDisplayValue(v, it.metric)));
    patch(i, { value: v });
  };

  // AP-9 — quick-fill the goal from what the PROGRAM prescribes (the mirror of
  // useAvg for the program's ask). `prescribed` is already muscle × hard-sets
  // gated above, so it's a bare count = the model value (toDisplayValue is a
  // no-op for hard-sets); guard on it being present and positive.
  const usePrescribed = (i: number) => {
    const it = items[i];
    if (prescribed == null || prescribed <= 0 || !it) return;
    const v = Math.max(1, Math.round(prescribed));
    setValueText(String(toDisplayValue(v, it.metric)));
    patch(i, { value: v });
  };

  const rowSummary = (it: TargetItem): string => {
    const parts = [keyLabel(it.axis, it.key), displayValueWithUnit(it), bandLabel(it)];
    if (!isScored(it)) {
      parts.push(t("composer.targetNotScored", { defaultValue: "not in meter yet" }));
    }
    return parts.join(", ");
  };

  return {
    editingIdx,
    setEditingIdx,
    valueText,
    belowText,
    aboveText,
    belowInvalid,
    aboveInvalid,
    stats,
    prescribed,
    keyLabel,
    unitLabel,
    displayValueWithUnit,
    formatMetricValue,
    bandLabel,
    rowSummary,
    beginEdit,
    addTarget,
    remove,
    changeAxis,
    axisDisabled,
    changeMetric,
    metricTaken,
    keyOptions,
    useAvg,
    usePrescribed,
    onValueChange,
    onValueBlur,
    onBelowChange,
    onAboveChange,
    onBandBlur,
    setKey: (i: number, k: string) => patch(i, { key: k }),
  };
}
